import { useState } from "react";
import { useAtomValue } from "jotai";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, ErrorState, Icon, Spinner } from "@common/index";
import { ComponentColor } from "@common/types";
import { ROUTES } from "@routing/routes";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { useMountRead } from "@/hooks/useMountRead";
import { locationsAtom } from "@/store/locationAtoms";
import { LocationStatus, type LocationResponse } from "@/models/location";
import {
    LocationFormModal,
    LOCATION_FORM_MODAL_ID,
} from "./_components/LocationFormModal";
import {
    DeleteLocationModal,
    DELETE_LOCATION_MODAL_ID,
} from "./_components/DeleteLocationModal";
import { UploadCertificateModal } from "./_components/UploadCertificateModal";
import { LocationStatusNotice } from "./_components/LocationStatusNotice";
import { canTakeCertificateUpload } from "./_helpers/canTakeCertificateUpload";

const openDialog = (id: string) => {
    const modal = document.getElementById(id) as HTMLDialogElement | null;
    modal?.showModal();
};

// One of the customer's locations (#320, DecisionLog §1.3): its address,
// approval status, certificate and actions in one place, rather than spread
// over a list row and its modals.
//
// There is no customer read of ONE location that carries its status:
// GET /locations/{id} is admin-only, and GET /locations/me/{id} has no status,
// rejection reason or certificate flag. So the page reads the customer's list,
// as the Locations tab does, and picks this location from it by id. That list
// holds only the caller's own active locations, so an unknown id, another
// customer's location and a removed one all come back as "not found".
export const AccountLocationDetail = () => {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { getLocations } = useLocationApi();
    const locations = useAtomValue(locationsAtom);
    // The status of the read THIS page issued (#407), never the shared
    // location atoms. Every re-read after an action goes through `reload`.
    const { readStatus, reload } = useMountRead(getLocations);
    const { showSuccess } = useNotification();

    const [editing, setEditing] = useState<LocationResponse | undefined>();
    const [deleting, setDeleting] = useState<LocationResponse | undefined>();
    const [isUploadOpen, setIsUploadOpen] = useState(false);

    const location = locations.find((candidate) => candidate.id === id);

    // As on the list: with the location in hand, a re-read in flight or
    // failed leaves it on screen. Without it, the page says what this mount's
    // read came to, and only a read that LOADED can say the location is not
    // there.
    if (!location) {
        return (
            <div className="bg-base-100 text-neutral min-h-[60vh]">
                <div className="max-w-3xl mx-auto px-4 py-6 md:py-8">
                    {readStatus === "loading" && (
                        <div className="flex justify-center items-center py-12">
                            <Spinner size="lg" />
                        </div>
                    )}

                    {readStatus === "failed" && (
                        <ErrorState onRetry={reload} />
                    )}

                    {readStatus === "loaded" && (
                        <div className="flex flex-col items-center gap-4 py-12 text-center">
                            <h1 className="text-2xl font-bold text-neutral">
                                Location not found
                            </h1>
                            <p className="text-neutral/70">
                                It may have been removed, or the link may be
                                out of date.
                            </p>
                            <Button
                                to={ROUTES.ACCOUNT_LOCATIONS}
                                color={ComponentColor.Warning}
                                className="text-neutral font-bold normal-case"
                            >
                                Back to locations
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const handleEdit = () => {
        setEditing(location);
        openDialog(LOCATION_FORM_MODAL_ID);
    };

    const handleDelete = () => {
        setDeleting(location);
        openDialog(DELETE_LOCATION_MODAL_ID);
    };

    // The upload modal closes on success and the toast confirms, as on the
    // list. The re-read brings the server's certificate state onto the page.
    const handleCertificateUploaded = (locationName: string) => {
        setIsUploadOpen(false);
        void reload();
        showSuccess({
            message: `Resale certificate uploaded for ${locationName}. Our team will review it.`,
        });
    };

    // OB-09: the re-read moves the location to Pending. A re-read that fails
    // leaves the page as it was, and the toast confirms the resubmit.
    const handleResubmitted = () => {
        void reload();
        showSuccess({
            message: `${location.name} resubmitted. Our team will review it.`,
        });
    };

    // The same rule as the upload modal's location list.
    const canUploadCertificate = canTakeCertificateUpload(location);

    return (
        <div className="bg-base-100 text-neutral min-h-[60vh]">
            <div className="max-w-3xl mx-auto px-4 py-6 md:py-8 flex flex-col gap-6">
                <Link
                    to={ROUTES.ACCOUNT_LOCATIONS}
                    className="link link-primary flex items-center gap-1 text-sm w-fit"
                >
                    <Icon name="arrow_back" />
                    All locations
                </Link>

                <div className="flex items-start justify-between gap-4">
                    <h1 className="text-3xl font-bold text-neutral">
                        {location.name}
                    </h1>
                    <div className="flex gap-2 shrink-0">
                        <Button
                            color={ComponentColor.Primary}
                            onClick={handleEdit}
                            className="text-neutral"
                        >
                            <Icon name="edit" />
                            Edit
                        </Button>
                        <Button
                            color={ComponentColor.Error}
                            onClick={handleDelete}
                        >
                            <Icon name="delete" />
                            Delete
                        </Button>
                    </div>
                </div>

                <section
                    aria-labelledby="location-address-heading"
                    className="rounded-lg border border-base-300 bg-base-200 p-4"
                >
                    <h2
                        id="location-address-heading"
                        className="font-semibold text-neutral"
                    >
                        Address
                    </h2>
                    <p className="text-sm text-neutral/80">
                        {location.streetAddress}
                    </p>
                    <p className="text-sm text-neutral/80">
                        {location.city}, {location.state} {location.zipCode}
                    </p>
                    <p className="text-sm text-neutral/80">
                        {location.country}
                    </p>
                </section>

                <section
                    aria-labelledby="location-status-heading"
                    className="rounded-lg border border-base-300 bg-base-200 p-4"
                >
                    <h2
                        id="location-status-heading"
                        className="font-semibold text-neutral"
                    >
                        Approval status
                    </h2>
                    {/* The list row says nothing for these two; a page about
                        the location has to. */}
                    {location.status === LocationStatus.Approved && (
                        <p className="mt-2 flex items-start gap-1 text-sm text-neutral">
                            <Icon name="check_circle" />
                            <span>Approved</span>
                        </p>
                    )}
                    {location.status === LocationStatus.Suspended && (
                        <p className="mt-2 flex items-start gap-1 text-sm text-neutral">
                            <Icon name="block" />
                            <span>
                                Suspended. This location can&apos;t be used for
                                orders.{" "}
                                <Link
                                    to={ROUTES.SUPPORT}
                                    className="link link-primary"
                                >
                                    Contact support
                                </Link>{" "}
                                to find out more.
                            </span>
                        </p>
                    )}
                    <LocationStatusNotice
                        location={location}
                        onResubmitted={handleResubmitted}
                    />
                </section>

                <section
                    aria-labelledby="location-certificate-heading"
                    className="rounded-lg border border-base-300 bg-base-200 p-4 flex flex-col items-start gap-3"
                >
                    <div>
                        <h2
                            id="location-certificate-heading"
                            className="font-semibold text-neutral"
                        >
                            Resale certificate
                        </h2>
                        <p className="text-sm text-neutral/80">
                            {location.hasCertificate
                                ? "A resale certificate is on file."
                                : "No resale certificate on file yet."}
                        </p>
                    </div>
                    {canUploadCertificate && (
                        <Button
                            color={ComponentColor.Primary}
                            onClick={() => setIsUploadOpen(true)}
                            className="normal-case"
                        >
                            <Icon name="upload_file" />
                            {location.hasCertificate
                                ? "Upload a new certificate"
                                : "Upload certificate"}
                        </Button>
                    )}
                </section>
            </div>

            {/* Edit only: this page never creates, so no organization. */}
            <LocationFormModal
                location={editing}
                onSaved={() => void reload()}
                onClose={() => setEditing(undefined)}
            />
            {/* GET /locations no longer lists a deleted location, so the page
                goes to the list rather than to "not found", and replaces its
                own history entry so Back does not return to it. */}
            <DeleteLocationModal
                location={deleting}
                onDeleted={() =>
                    navigate(ROUTES.ACCOUNT_LOCATIONS, { replace: true })
                }
                onClose={() => setDeleting(undefined)}
            />
            {isUploadOpen && (
                <UploadCertificateModal
                    initialLocationId={location.id}
                    onUploaded={handleCertificateUploaded}
                    onClose={() => setIsUploadOpen(false)}
                />
            )}
        </div>
    );
};
